import { useMemo } from 'react';
import type { MultiValue } from 'react-select';

import { Button, Icon, Select, type SelectOption, showToast } from '@repo/ui';
import { DataTable, type DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import { useTrustClients, useTrustTransactions, useTrustExport } from '../../../queries';
import { TRUST_STATUS_OPTIONS } from '../../../constants';
import type { TrustFiltersState } from '../../../queries/trustReconciliationQuery';
import { numberUtil } from '@xtnd/web/shared/utils';
import { useReportFilters } from '../../../hooks';

const FLAG_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  OK: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  MISSING_IN_QBO_BANK: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  MISSING_IN_QBO_LIABILITY: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
  },
  MISSING_IN_BOTH_QBO: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
  },
  MISSING_IN_CLIO: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
  },
};

const FLAG_LABELS: Record<string, string> = {
  OK: 'OK',
  MISSING_IN_QBO_BANK: 'Missing QBO Bank',
  MISSING_IN_QBO_LIABILITY: 'Missing QBO Liability',
  MISSING_IN_BOTH_QBO: 'Missing Both QBO',
  MISSING_IN_CLIO: 'Missing in Clio',
};

/**
 * Build QBO link by model (from API):
 * - qboJournalEntry: https://qbo.intuit.com/app/journal?txnId={id}
 * - qboPurchase: https://qbo.intuit.com/app/check?txnId={id}
 * Extracts txnId from transactionId (format "model#id" or plain id).
 */
function getQboTransactionUrl(
  transactionId: string | undefined,
  model?: 'qboJournalEntry' | 'qboPurchase'
): string {
  if (!transactionId) return '#';
  const parts = transactionId.split('#');
  const txnId = parts.length > 1 ? parts[1]! : parts[0]!;
  const base = 'https://qbo.intuit.com/app';
  if (model === 'qboPurchase') {
    return `${base}/check?txnId=${txnId}`;
  }
  return `${base}/journal?txnId=${txnId}`;
}

export function TrustTransactionTable() {
  const { filters, pagination, updateFilters, updatePagination } =
    useReportFilters<TrustFiltersState>();

  // Fetch data
  const { data, isLoading, error, refetch } = useTrustTransactions(filters, pagination);

  // Export mutation
  const exportMutation = useTrustExport();

  const handleExport = async () => {
    try {
      await exportMutation.mutateAsync({
        type: 'csv',
        clientIds: filters?.clients && filters.clients.length > 0 ? filters.clients : undefined,
      });
    } catch (error) {
      // Error is handled in mutation hook
      console.error('Export failed:', error);
    }
  };

  // Get available clients for filters
  const { data: clientsData, isLoading: clientsLoading } = useTrustClients();

  const availableClients = useMemo(() => {
    if (!clientsData) return [];
    return clientsData.map((client: any) => ({
      id: client.clientId || client.id,
      name: client.clientName || client.name,
    }));
  }, [clientsData]);

  // Convert available clients to options format
  const clientOptions: SelectOption[] = useMemo(() => {
    return availableClients.map(client => ({
      value: client.id,
      label: client.name,
    }));
  }, [availableClients]);

  // Get selected client options
  const selectedClientOptions: SelectOption[] = useMemo(() => {
    if (!filters || !filters?.clients) return [];
    return availableClients
      .filter(client => filters.clients.includes(client.id))
      .map(client => ({
        value: client.id,
        label: client.name,
      }));
  }, [availableClients, filters]);

  // Get selected status options
  const selectedStatusOptions: SelectOption[] = useMemo(() => {
    if (!filters || !filters.flags) return [];
    return filters.flags.map(flag => {
      const matchingOption = TRUST_STATUS_OPTIONS.find(
        (option: SelectOption) => option.value === flag
      );
      return (
        (matchingOption as SelectOption) || {
          value: flag,
          label: flag,
        }
      );
    });
  }, [filters]);

  // Handle client selection change
  const handleClientChange = (newValue: MultiValue<SelectOption> | null) => {
    const clients = newValue?.map(option => option.value) || [];
    const newFilters: TrustFiltersState = {
      ...filters,
      clients,
    };
    updateFilters(newFilters, true); // Reset page to 1 when filters change
  };

  // Handle status selection change
  const handleStatusChange = (newValue: MultiValue<SelectOption> | null) => {
    const selectedFlags = (newValue?.map((option: SelectOption) => option.value) || []) as string[];
    const newFilters: TrustFiltersState = {
      ...filters,
      flags: selectedFlags,
    };
    updateFilters(newFilters, true); // Reset page to 1 when filters change
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return numberUtil.formatCurrency(amount);
  };

  // Define columns for DataTable
  const columns: DataTableColumn<any>[] = useMemo(
    () => [
      {
        key: 'date',
        header: 'Date',
        align: 'left',
        renderCell: (txn: any) => (
          <span className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
            {new Date(txn.transactionDate).toLocaleDateString()}
          </span>
        ),
      },
      {
        key: 'client',
        header: 'Client',
        align: 'left',
        renderCell: (txn: any) => (
          <div className="space-y-1">
            {txn.clio?.clientName && (
              <div className="text-xs text-blue-600 dark:text-blue-400">
                Clio: {txn.clio.clientName}
              </div>
            )}
            {(txn.qboBank?.customerName || txn.qboLiability?.customerName) && (
              <div className="text-xs text-green-600 dark:text-green-400">
                QBO: {txn.qboBank?.customerName || txn.qboLiability?.customerName}
              </div>
            )}
            {!txn.clio?.clientName &&
              !txn.qboBank?.customerName &&
              !txn.qboLiability?.customerName && (
                <div className="text-sm text-xtnd-dark-400">{txn.clientName}</div>
              )}
          </div>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        align: 'left',
        renderCell: (txn: any) => (
          <div className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 max-w-xs">
            <div className="truncate" title={txn.description}>
              {txn.description || '-'}
            </div>
          </div>
        ),
      },
      {
        key: 'clio',
        header: (
          <div className="flex items-center justify-end gap-1">
            <Icon name="HiDatabase" className="h-3 w-3" />
            Clio
          </div>
        ),
        align: 'right',
        headerClassName: 'text-blue-700 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/30',
        renderCell: (txn: any) =>
          txn.clio ? (
            <div className="flex items-center justify-end gap-2">
              <span className="text-blue-600 dark:text-blue-400 font-medium">
                {formatCurrency(txn.clio.amount)}
              </span>
              <Icon name="HiCheckCircle" className="h-4 w-4 text-blue-500" />
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <span className="text-xtnd-dark-300 dark:text-xtnd-dark-500">-</span>
              <Icon name="HiXCircle" className="h-4 w-4 text-red-400" />
            </div>
          ),
      },
      {
        key: 'qboBank',
        header: (
          <div className="flex items-center justify-end gap-1">
            <Icon name="HiCash" className="h-3 w-3" />
            QBO Bank
          </div>
        ),
        align: 'right',
        headerClassName: 'text-green-700 dark:text-green-200 bg-green-50 dark:bg-green-900/30',
        renderCell: (txn: any) =>
          txn.qboBank ? (
            <div className="flex items-center justify-end gap-2">
              <span className="text-green-600 dark:text-green-400 font-medium">
                {formatCurrency(txn.qboBank.amount)}
              </span>
              <Icon name="HiCheckCircle" className="h-4 w-4 text-green-500" />
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <span className="text-xtnd-dark-300 dark:text-xtnd-dark-500">-</span>
              <Icon name="HiXCircle" className="h-4 w-4 text-red-400" />
            </div>
          ),
      },
      {
        key: 'qboLiability',
        header: (
          <div className="flex items-center justify-end gap-1">
            <Icon name="HiDocumentText" className="h-3 w-3" />
            QBO Liability
          </div>
        ),
        align: 'right',
        headerClassName: 'text-purple-700 dark:text-purple-200 bg-purple-50 dark:bg-purple-900/30',
        renderCell: (txn: any) =>
          txn.qboLiability ? (
            <div className="flex items-center justify-end gap-2">
              <span className="text-purple-600 dark:text-purple-400 font-medium">
                {formatCurrency(txn.qboLiability.amount)}
              </span>
              <Icon name="HiCheckCircle" className="h-4 w-4 text-purple-500" />
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <span className="text-xtnd-dark-300 dark:text-xtnd-dark-500">-</span>
              <Icon name="HiXCircle" className="h-4 w-4 text-red-400" />
            </div>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        align: 'center',
        renderCell: (txn: any) => {
          const flagColor = FLAG_COLORS[txn.flag] || {
            bg: 'bg-xtnd-dark-50',
            text: 'text-xtnd-dark-600',
            border: 'border-xtnd-dark-100',
          };
          const flagLabel = FLAG_LABELS[txn.flag] || txn.flag;
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${flagColor.bg} ${flagColor.text} ${flagColor.border} border`}
              title={flagLabel}
            >
              {flagLabel}
            </span>
          );
        },
      },
    ],
    []
  );

  const renderExpandedRow = (txn: any) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Clio Details */}
      <div className="border border-blue-200 dark:border-blue-900 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-2">
          <Icon name="HiDatabase" className="h-4 w-4" />
          Clio Transaction
        </h4>
        {txn.clio ? (
          <dl className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Transaction ID:</dt>
              <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 font-mono text-right">
                {txn.clio.transactionId}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Amount:</dt>
              <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 font-semibold">
                {formatCurrency(txn.clio.amount)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Date:</dt>
              <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
                {new Date(txn.clio.date || '').toLocaleDateString()}
              </dd>
            </div>
            {txn.clio.accountName && (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Account:</dt>
                <dd className="text-sm text-right">
                  {txn.clio.accountId ? (
                    <a
                      href={`https://app.clio.com/nc/#/bank_accounts/${txn.clio.accountId}/transactions`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline inline-flex items-center gap-1"
                    >
                      <span>{txn.clio.accountName}</span>
                      <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
                    </a>
                  ) : (
                    <span className="text-xtnd-dark-800 dark:text-xtnd-dark-50">{txn.clio.accountName}</span>
                  )}
                </dd>
              </div>
            )}
            {txn.clio.description && (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Description:</dt>
                <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 text-right">
                  {txn.clio.description}
                </dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
            <Icon name="HiXCircle" className="h-4 w-4" />
            No matching transaction found
          </p>
        )}
      </div>

      {/* QBO Bank Details */}
      <div className="border border-green-200 dark:border-green-900 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-3 flex items-center gap-2">
          <Icon name="HiCash" className="h-4 w-4" />
          QBO Trust Bank
        </h4>
        {txn.qboBank ? (
          <dl className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Transaction ID:</dt>
              <dd className="text-sm font-mono text-right">
                <a
                  href={getQboTransactionUrl(txn.qboBank.transactionId, txn.qboBank.model)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:underline inline-flex items-center gap-1"
                >
                  <span>{txn.qboBank.transactionId}</span>
                  <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
                </a>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Amount:</dt>
              <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 font-semibold">
                {formatCurrency(txn.qboBank.amount)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Date:</dt>
              <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
                {new Date(txn.qboBank.date || '').toLocaleDateString()}
              </dd>
            </div>
            {txn.qboBank.accountName && (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Account:</dt>
                <dd className="text-sm text-right">
                  <a
                    href={`https://qbo.intuit.com/app/register?accountId=${txn.qboBank.accountId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:underline inline-flex items-center gap-1"
                  >
                    <span>{txn.qboBank.accountName}</span>
                    <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
                  </a>
                </dd>
              </div>
            )}
            {txn.qboBank.description && (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Description:</dt>
                <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 text-right">
                  {txn.qboBank.description}
                </dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
            <Icon name="HiXCircle" className="h-4 w-4" />
            No matching transaction found
          </p>
        )}
      </div>

      {/* QBO Liability Details */}
      <div className="border border-purple-200 dark:border-purple-900 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-purple-600 dark:text-purple-400 mb-3 flex items-center gap-2">
          <Icon name="HiDocumentText" className="h-4 w-4" />
          QBO Trust Liability
        </h4>
        {txn.qboLiability ? (
          <dl className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Transaction ID:</dt>
              <dd className="text-sm font-mono text-right">
                <a
                  href={getQboTransactionUrl(
                    txn.qboLiability.transactionId,
                    txn.qboLiability.model
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 hover:underline inline-flex items-center gap-1"
                >
                  <span>{txn.qboLiability.transactionId}</span>
                  <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
                </a>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Amount:</dt>
              <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 font-semibold">
                {formatCurrency(txn.qboLiability.amount)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Date:</dt>
              <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
                {new Date(txn.qboLiability.date || '').toLocaleDateString()}
              </dd>
            </div>
            {txn.qboLiability.accountName && (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Account:</dt>
                <dd className="text-sm text-right">
                  <a
                    href={`https://qbo.intuit.com/app/register?accountId=${txn.qboLiability.accountId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 hover:underline inline-flex items-center gap-1"
                  >
                    <span>{txn.qboLiability.accountName}</span>
                    <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
                  </a>
                </dd>
              </div>
            )}
            {txn.qboLiability.description && (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Description:</dt>
                <dd className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 text-right">
                  {txn.qboLiability.description}
                </dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
            <Icon name="HiXCircle" className="h-4 w-4" />
            No matching transaction found
          </p>
        )}
      </div>

      {/* Reconciliation Info */}
      {txn.notes && (
        <div className="md:col-span-3 mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-400">
            <strong>Note:</strong> {txn.notes}
          </p>
        </div>
      )}
    </div>
  );

  const renderHeader = () => (
    <div className="border-b border-xtnd-light dark:border-xtnd-dark-700 pb-4 mb-6">
      <div className="flex flex-wrap items-start gap-2 md:items-center md:flex-nowrap">
        {/* Client Filter */}
        <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
          <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
            Client:
          </span>
          <div className="flex-1">
            <Select<SelectOption>
              value={selectedClientOptions}
              onChange={handleClientChange}
              options={clientOptions}
              placeholder="Filter by client..."
              disabled={clientsLoading || isLoading}
              isLoading={clientsLoading || isLoading}
              multiple={true}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              clearable={true}
              searchable={true}
              menuPlacement="auto"
              menuPosition="fixed"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
          <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
            Status:
          </span>
          <div className="flex-1">
            <Select<SelectOption>
              value={selectedStatusOptions}
              onChange={handleStatusChange}
              options={TRUST_STATUS_OPTIONS}
              placeholder="Filter by status..."
              multiple={true}
              isLoading={isLoading}
              disabled={isLoading}
              classNamePrefix="react-select"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              clearable={true}
              searchable={true}
              menuPlacement="auto"
              menuPosition="fixed"
              showOptionBullets={true}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </div>
        </div>

        {/* Selected counters */}
        <div className="w-full md:basis-[10%] md:w-auto">
          <div className="flex flex-col items-start md:items-end gap-1 mt-2 md:mt-0">
            {filters?.clients && filters.clients.length > 0 && (
              <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light whitespace-nowrap">
                {filters.clients.length} client{filters.clients.length > 1 ? 's' : ''} selected
              </span>
            )}
            {filters?.flags && filters.flags.length > 0 && (
              <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light whitespace-nowrap">
                {filters.flags.length} status{filters.flags.length > 1 ? 'es' : ''} selected
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      error={error}
      onRetry={() => refetch()}
      pagination={pagination}
      onPaginationChange={updatePagination}
      renderHeader={renderHeader}
      renderAction={() => (
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={isLoading}
          loading={exportMutation.isPending}
          leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
        >
          Export CSV
        </Button>
      )}
      renderExpandedRow={renderExpandedRow}
      isRowExpandable={() => true}
      emptyState={{
        icon: 'HiClipboardList',
        title: 'No transaction data available',
        description: 'There are no transactions matching your current filters.',
      }}
      tableContainerClassName="overflow-y-auto relative"
      tableClassName="min-w-full divide-y divide-xtnd-dark-100 relative"
      stickyHeader={true}
      tableMaxHeight="calc(100vh-300px)"
      pageSizeOptions={[25, 50, 100, 200]}
      loadingRowCount={5}
      loadingColumnCount={7}
      getRowKey={(txn: any) => txn.id}
    />
  );
}
